import { Box, Button, Typography } from '@mui/material';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import { tokens } from '../../theme';
import type { ErrorStateProps } from './types';

/**
 * A request failed, said once and in the middle of the page.
 *
 * **It replaces the page's content, not the page.** The AppBar and the
 * lifecycle rail stay where they are, so the reader keeps their bearings and
 * can go somewhere else without a back button.
 *
 * **`role="alert"`**: this appears in place of content that was loading, so
 * nothing else announces the change. Without it a screen reader user is left on
 * a page that silently stopped.
 *
 * The button is the theme's primary rather than the design's near-black. There
 * is no black in the token set, and inventing one for a single button is how a
 * palette starts to have exceptions in it.
 *
 * **The icon takes `accent.errorText`, not `action.primary`.** The brand red is
 * 4.31:1 on the icon tile's `accent.brandSurface` — below AA — and no lighter
 * or darker tint fixes that; only a darker red does.
 */
export const ErrorState = ({ title, message, onRetry }: ErrorStateProps) => (
  <Box
    role="alert"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      gap: 4,
      paddingBlock: 12,
      // Centred in the space left under the chrome rather than sitting just
      // beneath it. `50vh` is a floor rather than a height: a long message
      // still grows the box, and on a short viewport the content wins.
      minHeight: '50vh',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 48,
        height: 48,
        borderRadius: `${String(tokens.radius.md)}px`,
        backgroundColor: 'accent.brandSurface',
      }}
    >
      {/* Decoration: the heading below already says what happened. */}
      <ErrorOutlinedIcon aria-hidden sx={{ color: 'accent.errorText' }} />
    </Box>

    <Box>
      <Typography variant="h3" component="h2">
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', marginTop: 2, maxWidth: 420 }}>
        {message}
      </Typography>
    </Box>

    {onRetry !== undefined && (
      <Button variant="contained" onClick={onRetry}>
        Try again
      </Button>
    )}
  </Box>
);
